<template>
  <div class="form-group">
    <label for="client">
      {{ $t('users.customer') }}:
      <br v-if="selectedCustomer">
      <small v-if="selectedCustomer">
        {{ $t('customer.lastTaxVerification') }}
        <span
          v-if="selectedCustomer.tax_number_verified_at"
          class="text-primary">
          {{ selectedCustomer.tax_number_verified_at }}
        </span>
        <span
          v-else
          class="text-danger">
          {{ $t('ui.lack') }}
        </span>

        <a
          href="#verify-tax-number"
          class="ml-2 text-info"
          @click.prevent="verifyCustomerTaxID(false)">
          <div
            v-if="checkingTaxNumber"
            class="spinner-border spinner-border-sm"
            role="status"></div>
          <template v-else>
            <i class="material-icons" style="position: relative; top: 3px;">refresh</i>
            {{ $t('customer.verifyTaxID') }}
          </template>
        </a>

        <span
          v-if="selectedCustomer.tax_number_verified_at"
          class="px-2"> | </span>
        <template v-if="selectedCustomer.tax_number_verified_at">
          {{ $t('customer.retrievedStatus') }}
        </template>
        <span
          v-if="selectedCustomer.tax_number_verified_at && selectedCustomer.tax_number_verified_status === 'VALID'"
          class="text-success">
          {{ $t('customer.validTaxNumber') }}
        </span>
        <span
          v-if="selectedCustomer.tax_number_verified_at && selectedCustomer.tax_number_verified_status === 'INVALID'"
          class="text-danger">
          {{ $t('customer.invalidTaxNumber') }}
        </span>
        <template v-if="selectedCustomer.latest_verified_tax_number">
          ({{ $t('customer.retrievedStatusFor') }}{{ selectedCustomer.latest_verified_tax_number }})
        </template>

        <template v-if="selectedCustomer.latest_verified_tax_number && selectedCustomer.latest_verified_tax_number.substr(0, 2) !== 'PL' && selectedCustomer.tax_number_verified_status === 'VALID'">
          <span class="px-2"> | </span>
          <a
            v-if="!downloadingTaxIDConfirmationPdf"
            href="#download-tax-info"
            @click.prevent="downloadTaxNumberConfirmation">
            <i class="material-icons">cloud_download</i>
            {{ $t('customer.downloadTaxNumberConfirmation') }}
          </a>
          <span v-else>
            <div class="spinner-border spinner-border-sm" role="status"></div>
            {{ $t('customer.downloadTaxNumberConfirmationDownloading') }}
          </span>
        </template>
      </small>
    </label>

    <validation-provider
      rules="required"
      name="customer_id"
      class="w-100"
      v-slot="{ errors }">
      <div :class="{ 'form-control': true, 'is-select': true, 'is-invalid': errors.length }">
        <v-select
          :options="customers"
          v-model="customer_id"
          :reduce="customer => customer.id"
          :filterBy="filterByForCustomers"
          :placeholder="$t('customer.selectCustomer')"
          label="name"
          @search="debouncedFetchCustomers">
          <template slot="no-options">
            <template v-if="!emptyResults">{{ $t('ui.searching') }}&hellip;</template>
            <template v-if="emptyResults">{{ $t('customer.noCustomerMarchingPhraseFound') }}&hellip;</template>
          </template>
          <template slot="option" slot-scope="option">
            <div class="d-center">
              {{ option.name }} ({{ option.invoice_country_prefix }}{{ option.invoice_tax_number }})
            </div>
          </template>
          <template slot="selected-option" slot-scope="option">
            <div class="selected d-center">
              {{ option.name }} ({{ option.invoice_country_prefix }}{{ option.invoice_tax_number }})
            </div>
          </template>
        </v-select>
      </div>
      <div class="invalid-feedback">
        {{ errors[0] }}
      </div>
    </validation-provider>

    <ul
      v-if="!userIsCustomer && selectedCustomer && selectedCustomer.tags"
      class="customer-tags-list mt-2 mb-2">
      <li
        v-for="(tag, index) of selectedCustomer.tags"
        :key="'customer-tag-' + index"
        class="mr-2">
        <d-badge :style="'background-color:' + tag.color + '; color: ' + contrastColor(tag.color) + ';'">
          {{ tag.name }}
        </d-badge>
      </li>
    </ul>

    <d-badge
      v-if="selectedCustomer && includeDiscounts"
      outline
      theme="dark"
      class="w-100 text-left mt-2 d-flex">
      <strong>{{ $t('customer.discount.currentCustomerDiscounts') }}:</strong>
      <span class="pl-4">{{ $t('product.products') }}: </span>
      <strong :class="{'text-danger': !parseFloat(customerProductsDiscount), 'text-success': parseFloat(customerProductsDiscount) }">{{ customerProductsDiscount }}%</strong>
      <span class="pl-4">{{ $t('service.services') }}: </span>
      <strong :class="{'text-danger': !parseFloat(customerServicesDiscount), 'text-success': parseFloat(customerServicesDiscount) }">{{ customerServicesDiscount }}%</strong>
      <span class="pl-4">{{ $t('order.delivery') }}: </span>
      <strong :class="{'text-danger': !parseFloat(customerShippingDiscount), 'text-success': parseFloat(customerShippingDiscount) }">{{ customerShippingDiscount }}%</strong>

      <a
        href="#"
        class="ml-auto"
        @click.prevent="$bus.$emit('show-customer-discounts-popup')">
        {{ $t('customer.discount.editCustomerDiscounts') }}
      </a>
    </d-badge>
  </div>
</template>

<script>
import debounce from 'lodash.debounce';
import FormUtils from './../../utils/FormUtils.js';
import Vue from 'vue';

export default {
  name: 'order-select-customer',
  props: {
    includeDiscounts: {
      type: Boolean,
      default: true
    },
    userIsCustomer: {
      type: Boolean,
      default: false
    },
    isIndent: {
      type: Boolean,
      default: false
    },
    orderDataInvoice: {
      type: Object,
      default: null
    }
  },
  watch: {
    customer_id (newValue) {
      if (newValue !== 0 && newValue !== null) {
        let customerData = this.customers.filter(customer => customer.id === newValue);

        if (customerData[0]) {
          this.selectedCustomer = JSON.parse(JSON.stringify(customerData[0]));

          if (!this.skipCustomerCheck) {
            this.verifyCustomerTaxID(false);
          }

          if (this.isIndent) {
            if (this.selectedCustomer.indent_discount_enabled) {
              this.$bus.$emit('order-enable-customer-discounts');
            } else {
              this.$bus.$emit('order-disable-customer-discounts');
            }
          }

          if (customerData && customerData[0] && customerData[0].auto_special_requirement) {
            this.$bus.$emit('ui-select-customer-auto-special-requirement', true);
            this.$bus.$emit('ui-select-customer-set-special-requirement', customerData[0].special_requirement);
          } else {
            this.$bus.$emit('ui-select-customer-auto-special-requirement', false);
            this.$bus.$emit('ui-select-customer-set-special-requirement', '');
          }
        } else {
          this.selectedCustomer = null;
          this.customer_id = null;
        }
      } else {
        this.selectedCustomer = null;
      }

      this.updateDiscounts();

      Vue.nextTick(() => {
        this.$bus.$emit('order-update-shipment-cost', true);
        this.$bus.$emit('order-customer-id-changed');
        this.$bus.$emit('order-update-shipment-address-id');
      });
    },
    includeDiscounts (newValue) {
      if (this.selectedCustomer && newValue) {
        this.customerProductsDiscount = parseFloat(this.selectedCustomer.discount_products);
        this.customerServicesDiscount = parseFloat(this.selectedCustomer.discount_services);
        this.customerShippingDiscount = parseFloat(this.selectedCustomer.discount_shipping);
      } else {
        this.customerProductsDiscount = 0;
        this.customerServicesDiscount = 0;
        this.customerShippingDiscount = 0;
      }

      Vue.nextTick(() => {
        this.$bus.$emit('order-update-shipment-cost', true);
      });
    }
  },
  data () {
    return {
      customer_id: null,
      customers: [],
      customerProductsDiscount: 0,
      customerServicesDiscount: 0,
      customerShippingDiscount: 0,
      emptyResults: false,
      selectedCustomer: null,
      downloadingTaxIDConfirmationPdf: false,
      checkingTaxNumber: false,
      lastRequestID: null,
      skipCustomerCheck: false
    }
  },
  mounted () {
    this.loadData();
    this.$bus.$on('order-select-customer-verify-tax-id', this.externalVerifyTaxID);
  },
  methods: {
    loadData () {
      this.lastRequestID = +new Date();
      let requestID = this.lastRequestID;

      FormUtils.loadAdditionalData(this, {
        requestID,
        method: 'get',
        endpoint: '/customers/items',
        outputKey: 'customers',
        errorKey: 'loadCustomersError'
      });
    },
    filterByForCustomers (option, label, search) {
      return (option.name + ' ' + option.email + ' ' + option.invoice_country_prefix + option.invoice_tax_number).toLowerCase().indexOf(search.toLowerCase()) > -1;
    },
    loadCustomer (customerID, addressID = false, contactID = false, skipDataReload = false) {
      this.skipCustomerCheck = true;
      this.lastRequestID = +new Date();
      let requestID = this.lastRequestID;

      FormUtils.loadAdditionalData(this, {
        requestID,
        method: 'get',
        endpoint: '/customers/item/' + parseInt(customerID, 10),
        outputKey: 'customers',
        errorKey: 'loadCustomersError',
        wrapResponseIntoArray: true,
        successAction: () => {
          if (skipDataReload && this.customer_id) {
            let customerData = this.customers.filter(customer => customer.id === this.customer_id);

            if (customerData[0]) {
              this.selectedCustomer = JSON.parse(JSON.stringify(customerData[0]));
            }

            this.updateDiscounts(true);
          }

          this.customer_id = parseInt(customerID, 10);

          Vue.nextTick(() => {
            this.skipCustomerCheck = false;
          });

          if (!skipDataReload) {
            Vue.nextTick(() => {
              setTimeout(() => {
                this.$bus.$emit('order-data-fetch-success', 'customer');
                this.$bus.$emit('order-select-address-set-id', addressID);
                this.$bus.$emit('order-select-contact-set-id', contactID);
              }, 0);
            });
          }
        },
        catchAction: (err) => {
          if (err && err.response && err.response.status === 404) {
            Vue.swal({
              title: this.$t('order.nonExistingCustomerTitle'),
              html: this.$t('order.nonExistingCustomerText'),
              type: 'warning',
              confirmButtonText: this.$t('ui.ok'),
              buttonsStyling: true
            });

            Vue.set(this, 'customer_id', null);
            this.$bus.$emit('order-data-fetch-success', 'customer');
            this.$bus.$emit('order-select-address-set-id', null);
            this.$bus.$emit('order-select-contact-set-id', null);
            this.$bus.$emit('order-customer-has-been-reset');
          }
        }
      });
    },
    fetchCustomers (searchPhrase, loadingCallback) {
      this.emptyResults = false;
      loadingCallback(true);

      let endpointUrl = '/customers/items';
      let responseAsArray = false;
      let paramsData = {};

      if (searchPhrase !== '') {
        paramsData.where = {
          search: searchPhrase
        };
      }

      if (this.customer_id !== 0 && this.customer_id !== null && searchPhrase === '') {
        endpointUrl = '/customers/item/' + parseInt(this.customer_id, 10);
        responseAsArray = true;
      }

      this.lastRequestID = +new Date();
      let requestID = this.lastRequestID;

      FormUtils.loadAdditionalData(this, {
        requestID,
        method: 'get',
        endpoint: endpointUrl,
        outputKey: 'customers',
        errorKey: 'loadCustomersError',
        params: paramsData,
        wrapResponseIntoArray: responseAsArray,
        successAction: (response) => {
          if (!response.length) {
            this.emptyResults = true;
          }

          loadingCallback(false);
        }
      });
    },
    updateDiscounts (updateDiscounts = false) {
      if (this.selectedCustomer && this.includeDiscounts) {
        this.customerProductsDiscount = parseFloat(this.selectedCustomer.discount_products);
        this.customerServicesDiscount = parseFloat(this.selectedCustomer.discount_services);
        this.customerShippingDiscount = parseFloat(this.selectedCustomer.discount_shipping);

        if (updateDiscounts) {
          Vue.nextTick(() => {
            this.$bus.$emit('order-update-shipment-cost', true);
          });
        }
      } else {
        this.customerProductsDiscount = 0;
        this.customerServicesDiscount = 0;
        this.customerShippingDiscount = 0;
      }
    },
    // eslint-disable-next-line
    debouncedFetchCustomers: debounce(function (searchPhrase, loadingCallback) {
      this.fetchCustomers(searchPhrase, loadingCallback);
    }, 500),
    contrastColor (colorBg) {
      let threshold = 130;
      let r = parseInt(colorBg.replace('#', '').substring(0, 2), 16);
      let g = parseInt(colorBg.replace('#', '').substring(2, 4), 16);
      let b = parseInt(colorBg.replace('#', '').substring(4, 6), 16);
      let brightness = ((r * 299) + (g * 587) + (b * 114)) / 1000;

      if (brightness > threshold) {
        return '#000';
      }

      return '#fff';
    },
    externalVerifyTaxID (immediate) {
      this.verifyCustomerTaxID(true, immediate);
    },
    verifyCustomerTaxID (isExternalCall = false, immediate = false) {
      if (this.checkingTaxNumber) {
        return;
      }

      this.checkingTaxNumber = true;

      this.$http.get('/customers/verify-tax-number', {
        params: {
          id: parseInt(this.selectedCustomer.id, 10),
          prefix: this.selectedCustomer.invoice_country_prefix,
          number: this.selectedCustomer.invoice_tax_number.replace(this.selectedCustomer.invoice_country_prefix, '')
        }
      }).then(response => {
        this.checkingTaxNumber = false;

        if (response.data.verification && response.data.verification.is_exception) {
          Vue.swal({
            title: this.$t('ui.errorOccured'),
            html: this.$t('customer.viesCommunicationIssue'),
            type: 'error',
            confirmButtonText: this.$t('ui.ok'),
            buttonsStyling: true
          }).then(() => {
            if (isExternalCall) {
              this.$bus.$emit('show-gus-comparision-popup', {
                gusData: false,
                order: this.orderDataInvoice,
                customerID: parseInt(this.selectedCustomer.id, 10),
                taxVerificationID: false,
                isExternalCall,
                immediate
              });
            }
          });
        } else if (response.data.success) {
          if (response.data.integration_disabled === 1) {
            Vue.swal({
              title: this.$t('customer.verificationIntegrationDisabledTitle'),
              html: this.$t('customer.verificationIntegrationDisabledDesc'),
              type: 'warning',
              confirmButtonText: this.$t('ui.ok'),
              buttonsStyling: true
            }).then(() => {
              if (isExternalCall) {
                this.$bus.$emit('show-gus-comparision-popup', {
                  gus: false,
                  order: this.orderDataInvoice,
                  customerID: parseInt(this.selectedCustomer.id, 10),
                  taxVerificationID: false,
                  isExternalCall,
                  immediate
                });
              }
            });
          } else if (response.data.verification_skipped !== 1) {
            this.selectedCustomer.tax_number_verified_at = response.data.verification.created_at;
            this.selectedCustomer.tax_number_verified_status = response.data.verification.status;
            this.selectedCustomer.latest_verified_tax_number = this.selectedCustomer.invoice_tax_number;
            this.selectedCustomer.tax_number_verified_is_active = response.data.verification.is_active;

            if (response.data.verification.database === 'GUS' && response.data.verification.status === 'VALID') {
              let gusData = response.data.verification.response;
              gusData.taxNumber = response.data.verification.tax_number;

              this.$bus.$emit('show-gus-comparision-popup', {
                gus: gusData,
                order: this.orderDataInvoice,
                customer: this.selectedCustomer,
                customerID: parseInt(this.selectedCustomer.id, 10),
                taxVerificationID: parseInt(response.data.verification.id, 10),
                isExternalCall,
                immediate
              });
            } else if (response.data.verification.database === 'GUS' && response.data.verification.status === 'INVALID') {
              Vue.swal({
                title: this.$t('customer.verificationGusErrorTitle'),
                html: this.$t('customer.verificationGusErrorDesc'),
                type: 'warning',
                confirmButtonText: this.$t('ui.ok'),
                buttonsStyling: true
              });
            } else if (response.data.verification.database === 'VIES' && response.data.verification.status === 'INVALID') {
              Vue.swal({
                title: this.$t('customer.verificationViesErrorTitle'),
                html: this.$t('customer.verificationViesErrorDesc'),
                type: 'warning',
                confirmButtonText: this.$t('ui.ok'),
                buttonsStyling: true
              });
            } else {
              Vue.swal({
                title: this.$t('customer.verificationOtherErrorTitle'),
                html: this.$t('customer.verificationOtherErrorDesc'),
                type: 'warning',
                confirmButtonText: this.$t('ui.ok'),
                buttonsStyling: true
              });
            }
          } else {
            Vue.swal({
              title: this.$t('customer.verificationSkippedTitle'),
              html: this.$t('customer.verificationSkippedDesc'),
              type: 'warning',
              confirmButtonText: this.$t('ui.ok'),
              buttonsStyling: true
            }).then(() => {
              if (isExternalCall) {
                this.$bus.$emit('show-gus-comparision-popup', {
                  gus: false,
                  order: this.orderDataInvoice,
                  customerID: parseInt(this.selectedCustomer.id, 10),
                  taxVerificationID: false,
                  isExternalCall,
                  immediate
                });
              }
            });
          }
        } else {
          Vue.swal({
            title: this.$t('ui.errorOccured'),
            html: this.$t('ui.tryAgain'),
            type: 'warning',
            confirmButtonText: this.$t('ui.ok'),
            buttonsStyling: true
          });
        }
      }).catch(error => {
        console.log(error);
        Vue.swal({
          title: this.$t('ui.errorOccured'),
          html: this.$t('ui.tryAgain'),
          type: 'warning',
          confirmButtonText: this.$t('ui.ok'),
          buttonsStyling: true
        });
        this.checkingTaxNumber = false;
      });
    },
    downloadTaxNumberConfirmation () {
      this.downloadingTaxIDConfirmationPdf = true;

      this.$http.post('/customers/download-tax-number-verification/' + this.customer_id, {}, {
        responseType: 'blob'
      }).then(response => {
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        let filenamePrefix = this.$t('customer.taxNumberConfirmationFileNamePrefix');
        link.setAttribute('download', filenamePrefix + this.customer_id + '.pdf');
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        this.downloadingTaxIDConfirmationPdf = false;
      }).catch(error => {
        console.log(error);
        Vue.swal({
          title: this.$t('ui.errorOccuredDuringDownload'),
          html: this.$t('ui.tryAgain'),
          type: 'warning',
          confirmButtonText: this.$t('ui.ok'),
          buttonsStyling: true
        });
        this.downloadingTaxIDConfirmationPdf = false;
      });
    }
  },
  beforeDestroy () {
    this.$bus.$off('order-select-customer-verify-tax-id', this.externalVerifyTaxID);
  }
}
</script>

<style lang="scss" scoped>
.customer-tags-list {
  list-style-type: none;
  margin: 0;
  padding: 0;

  li {
    display: inline-block;
  }
}
</style>
